/**
 * PATH: packages/ui/src/kanban/mentionTokens.ts
 * FILE_ID: UI-KANBAN-MENTION-001
 * PURPOSE: Pure, framework-free engine for `@mention` parsing in the
 *          card composer — autocomplete detection + suggestion ranking
 *          while typing, and a final extract pass on submit.
 * WHY:
 *   Operator complaint (2026-06-03): typing a quick card like
 *   "Scaffolding @today get timber from bunnings @mika and @facu"
 *   did nothing — no autocomplete, no date, no member assignment.
 *   They expect `@name` to recommend teammates from the org registry
 *   and `@today` to set the due date, the way every modern task tool
 *   behaves.
 *
 * FLOW (two call sites share one engine):
 *   typing  → detectActiveMention → buildMentionSuggestions → applyMention
 *   submit  → extractMentions → { title, memberIds, dueDate }
 *
 * SAFE: no React, no I/O, no globals. `now` is always injected so the
 * date math is deterministic and testable.
 */

import type { KanbanMember } from './api'

export type MentionSuggestionKind = 'member' | 'date'

export interface MentionSuggestion {
  kind: MentionSuggestionKind
  /** Stable React key. */
  key: string
  /** Primary label (member name / date word). */
  label: string
  /** Muted secondary line (email / resolved date). */
  detail?: string
  /** Text inserted after the `@` when accepted (no leading `@`). */
  insertText: string
  /** Present when kind === 'member'. */
  memberId?: number
}

export interface ActiveMention {
  /** Index of the `@` in the source string. */
  start: number
  /** Query typed after `@` up to the caret (may be empty). */
  query: string
}

/** Chars that may form the body of a mention token (after `@`). */
const TOKEN_CHAR = /[A-Za-z0-9_]/
/** A mention only triggers at string start or after a separator —
 *  this keeps email addresses (`a@b.com`) from being treated as
 *  mentions. */
const BOUNDARY_BEFORE = /[\s([{<"'.,;:]/

/**
 * Given the full field text and the caret offset, return the active
 * mention the caret currently sits inside, or null. Input → Output:
 * (text, caret) → ActiveMention | null.
 */
export function detectActiveMention(text: string, caret: number): ActiveMention | null {
  if (caret < 0 || caret > text.length) return null
  let i = caret - 1
  while (i >= 0 && TOKEN_CHAR.test(text[i]!)) i -= 1
  if (i < 0 || text[i] !== '@') return null
  const before = i - 1
  if (before >= 0 && !BOUNDARY_BEFORE.test(text[before]!)) return null
  return { start: i, query: text.slice(i + 1, caret) }
}

// ---- date tokens ---------------------------------------------------

const WEEKDAY_NAMES = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
]
const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]
const WEEKDAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** Words offered as date suggestions (canonical, full spelling). */
export const DATE_SUGGESTION_WORDS = [
  'today', 'tomorrow', 'yesterday',
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
]

function toISODate(d: Date): string {
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const da = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${da}`
}

function addDays(base: Date, days: number): Date {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate())
  d.setDate(d.getDate() + days)
  return d
}

/**
 * Resolve a date word to a local `YYYY-MM-DD`, or null when unknown.
 * (token, now) → iso | null. Weekdays resolve to the soonest matching
 * day on/after `now`.
 */
export function parseDateToken(token: string, now: Date): string | null {
  const t = token.trim().toLowerCase()
  if (!t) return null
  if (['today', 'tod', 'tonight', 'eod'].includes(t)) return toISODate(now)
  if (['tomorrow', 'tmr', 'tom', 'tmrw'].includes(t)) return toISODate(addDays(now, 1))
  if (['yesterday', 'yest', 'yda'].includes(t)) return toISODate(addDays(now, -1))
  let wd = WEEKDAY_NAMES.indexOf(t)
  if (wd === -1) wd = WEEKDAY_ABBR.findIndex((a) => a.toLowerCase() === t)
  if (wd >= 0) {
    const diff = (wd - now.getDay() + 7) % 7
    return toISODate(addDays(now, diff))
  }
  return null
}

/** Human label for a resolved iso date, e.g. "Wed, 3 Jun". */
export function formatDateLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map((n) => Number(n))
  if (!y || !m || !d) return iso
  const dt = new Date(y, m - 1, d)
  return `${WEEKDAY_ABBR[dt.getDay()]}, ${d} ${MONTH_ABBR[m - 1]}`
}

// ---- member matching ----------------------------------------------

function memberCandidates(m: KanbanMember): string[] {
  const out: string[] = []
  const name = (m.name || '').toLowerCase().trim()
  if (name) {
    out.push(name.replace(/\s+/g, ''))
    for (const part of name.split(/\s+/)) if (part) out.push(part)
  }
  const local = (m.email || '').toLowerCase().split('@')[0]
  if (local) out.push(local)
  return out
}

/** First name (or email local part) used as the inserted handle. */
function memberHandle(m: KanbanMember): string {
  const first = (m.name || '').trim().split(/\s+/)[0]
  if (first) return first.replace(/[^A-Za-z0-9_]/g, '')
  const local = (m.email || '').split('@')[0]
  return local || `user${m.id}`
}

function memberMatchesQuery(m: KanbanMember, q: string): boolean {
  if (!q) return true
  return memberCandidates(m).some((c) => c.startsWith(q))
}

/**
 * Resolve a typed token to exactly one member, or null when there's
 * no match or it's ambiguous (never guess — a wrong assignment is
 * worse than none). (token, members) → KanbanMember | null.
 */
export function resolveMemberToken(
  token: string,
  members: KanbanMember[],
): KanbanMember | null {
  const q = token.trim().toLowerCase()
  if (!q) return null
  const hits = members.filter((m) => memberCandidates(m).some((c) => c.startsWith(q)))
  if (hits.length === 1) return hits[0]!
  if (hits.length > 1) {
    const exact = hits.filter((m) => memberCandidates(m).includes(q))
    if (exact.length === 1) return exact[0]!
  }
  return null
}

// ---- suggestion building (typing) ---------------------------------

export interface BuildSuggestionsOpts {
  now: Date
  /** Max rows in the dropdown. Default 8. */
  limit?: number
}

/**
 * Rank suggestions for the current query: members first (the headline
 * ask), then date words. (query, members, opts) → MentionSuggestion[].
 */
export function buildMentionSuggestions(
  query: string,
  members: KanbanMember[],
  opts: BuildSuggestionsOpts,
): MentionSuggestion[] {
  const q = query.trim().toLowerCase()
  const limit = opts.limit ?? 8
  const memberSugs: MentionSuggestion[] = members
    .filter((m) => memberMatchesQuery(m, q))
    .slice(0, 6)
    .map((m) => ({
      kind: 'member' as const,
      key: `member-${m.id}`,
      label: m.name || m.email || `User ${m.id}`,
      detail: m.role || m.email || undefined,
      insertText: memberHandle(m),
      memberId: m.id,
    }))
  const dateWords = q
    ? DATE_SUGGESTION_WORDS.filter((w) => w.startsWith(q))
    : ['today', 'tomorrow']
  const dateSugs: MentionSuggestion[] = dateWords.slice(0, 4).map((w) => {
    const iso = parseDateToken(w, opts.now)
    return {
      kind: 'date' as const,
      key: `date-${w}`,
      label: w,
      detail: iso ? formatDateLabel(iso) : undefined,
      insertText: w,
    }
  })
  return [...memberSugs, ...dateSugs].slice(0, limit)
}

/**
 * People-only suggestion builder for free-text surfaces (card
 * Description, Comments) where `@` means "tag a teammate" and date
 * tokens are NOT wanted (unlike the quick-add title composer, which
 * uses ``buildMentionSuggestions``). (query, members, limit) →
 * member ``MentionSuggestion[]``.
 */
export function buildMemberSuggestions(
  query: string,
  members: KanbanMember[],
  limit = 6,
): MentionSuggestion[] {
  const q = query.trim().toLowerCase()
  return members
    .filter((m) => memberMatchesQuery(m, q))
    .slice(0, limit)
    .map((m) => ({
      kind: 'member' as const,
      key: `member-${m.id}`,
      label: m.name || m.email || `User ${m.id}`,
      detail: m.role || m.email || undefined,
      insertText: memberHandle(m),
      memberId: m.id,
    }))
}

const MEMBER_MENTION_SCAN = /(^|[\s([{<"'.,;:])@([A-Za-z0-9_]+)/g

/**
 * Derive the set of member ids currently @-tagged in free text — the
 * source of truth for what to send with a Description/Comment save.
 * Re-scans the WHOLE text every call (rather than tracking inserts),
 * so it stays correct when the operator deletes a handle or reopens a
 * card whose saved body already contains ``@Name`` runs. Ambiguous
 * tokens (two "Mark"s) resolve to nobody — a wrong tag is worse than a
 * missing one. (text, members) → unique member ids.
 */
export function deriveMemberMentionIds(
  text: string,
  members: KanbanMember[],
): number[] {
  const ids: number[] = []
  const scan = new RegExp(MEMBER_MENTION_SCAN.source, 'g')
  let m: RegExpExecArray | null
  while ((m = scan.exec(text)) !== null) {
    const member = resolveMemberToken(m[2]!, members)
    if (member && !ids.includes(member.id)) ids.push(member.id)
  }
  return ids
}

// ---- apply (accept a suggestion) ----------------------------------

export interface ApplyResult {
  text: string
  caret: number
}

/**
 * Replace the active `@query` slice with the accepted token plus a
 * trailing space. (text, active, suggestion) → { text, caret }.
 */
export function applyMention(
  text: string,
  active: ActiveMention,
  suggestion: MentionSuggestion,
): ApplyResult {
  const before = text.slice(0, active.start)
  const after = text.slice(active.start + 1 + active.query.length)
  const inserted = `@${suggestion.insertText} `
  return { text: before + inserted + after, caret: before.length + inserted.length }
}

// ---- extract (submit) ---------------------------------------------

export interface ExtractedMentions {
  /** Title with recognised tokens stripped + whitespace tidied. */
  title: string
  /** Unique member ids resolved from `@name` tokens. */
  memberIds: number[]
  /** First resolved date token as `YYYY-MM-DD`, else null. */
  dueDate: string | null
}

const MENTION_SCAN = /(^|[\s([{<"'.,;:])@([A-Za-z0-9_]+)/g

/**
 * Final parse on submit. Recognised date words become `dueDate` and
 * `@name` tokens become `memberIds`; both are stripped from the title.
 * Unrecognised `@foo` is left as literal text. (raw, members, now) →
 * ExtractedMentions.
 */
export function extractMentions(
  raw: string,
  members: KanbanMember[],
  now: Date,
): ExtractedMentions {
  const memberIds: number[] = []
  let dueDate: string | null = null
  const removals: Array<[number, number]> = []
  MENTION_SCAN.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = MENTION_SCAN.exec(raw)) !== null) {
    const word = m[2]!
    const at = m.index + m[1]!.length
    const end = at + 1 + word.length
    if (!dueDate) {
      const iso = parseDateToken(word, now)
      if (iso) {
        dueDate = iso
        removals.push([at, end])
        continue
      }
    }
    const member = resolveMemberToken(word, members)
    if (member) {
      if (!memberIds.includes(member.id)) memberIds.push(member.id)
      removals.push([at, end])
    }
  }

  let title = raw
  if (removals.length > 0) {
    let out = ''
    let idx = 0
    for (const [s, e] of removals) {
      out += raw.slice(idx, s)
      idx = e
    }
    out += raw.slice(idx)
    out = out
      .replace(/\s{2,}/g, ' ')
      .replace(/\s+([.,;:])/g, '$1')
      .trim()
      // Drop a trailing conjunction left dangling by a removed token
      // ("… bunnings and" → "… bunnings").
      .replace(/[\s,]*\b(?:and|&|with)\s*$/i, '')
      .trim()
    // Never produce an empty title — fall back to the raw text so the
    // card is still nameable; members/date are still applied.
    title = out || raw.trim()
  }

  return { title, memberIds, dueDate }
}
